/**
 * Hook that adds a new line item to a contract extraction category.
 *
 * Calls the `addContractLineItem` mutation (SE-4382) which writes the
 * canonical line-item shape `{ text, isConflict: false, confidenceNote:
 * "manually added" }` to the extraction's `extractedData` JSONB column.
 * The GraphQL document is defined inline via `gql` (same pattern used
 * by the other extraction hooks in this feature) so it can ship before
 * codegen runs against the updated schema.
 * @remarks Refetches the `GetContractExtraction` query on success so
 * the newly added item renders in the review view immediately. This
 * hook is consumed by `ExtractionDataSectionContainer` inside
 * `SE-4342`'s conflict UI and will be consumed by SE-4384's full
 * inline edit UI once it lands.
 * @module features/contracts/hooks/useAddLineItem
 */

import { gql, useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { activeContractExtractionRefetch } from "./contractExtractionRefetch";

/** GraphQL document for the AddContractLineItem mutation. */
export const ADD_CONTRACT_LINE_ITEM_MUTATION = gql`
  mutation AddContractLineItem($input: AddContractLineItemInput!) {
    addContractLineItem(input: $input) {
      success
    }
  }
`;

/** Shape of the AddContractLineItem mutation response. */
interface AddContractLineItemResponse {
  readonly addContractLineItem: { readonly success: boolean };
}

/** Shape of the AddContractLineItem mutation variables. */
interface AddContractLineItemVariables {
  readonly input: {
    readonly extractionId: string;
    readonly category: string;
    readonly text: string;
  };
}

/** Public API returned by `useAddLineItem`. */
export interface UseAddLineItemResult {
  /**
   * Adds a line item to a category in the extraction data. On success,
   * refetches the `GetContractExtraction` query so the new item
   * appears in the review view.
   * @param extractionId - UUID of the extraction to update
   * @param category - Category key within `extractedData`
   * @param text - Single free-text value describing the new line item
   * @param playerId - Player UUID (needed for the extraction query refetch variables)
   * @param documentType - Document type (needed for the extraction query refetch variables)
   * @returns `true` when the server confirms success, `false` otherwise
   */
  readonly addLineItem: (
    extractionId: string,
    category: string,
    text: string,
    playerId: string,
    documentType: string
  ) => Promise<boolean>;
  /** Whether an add request is currently in flight. */
  readonly isAdding: boolean;
}

/**
 * Wraps the AddContractLineItem mutation with loading state and an
 * automatic refetch of the extraction query on success.
 * @returns Imperative add function + loading state
 */
export function useAddLineItem(): UseAddLineItemResult {
  const [runMutation] = useMutation<
    AddContractLineItemResponse,
    AddContractLineItemVariables
  >(ADD_CONTRACT_LINE_ITEM_MUTATION);

  const [isAdding, setIsAdding] = useState(false);

  const addLineItem = useCallback(
    async (
      extractionId: string,
      category: string,
      text: string,
      playerId: string,
      documentType: string
    ): Promise<boolean> => {
      setIsAdding(true);
      try {
        const { data } = await runMutation({
          variables: { input: { extractionId, category, text } },
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
          ],
          awaitRefetchQueries: true,
        });
        return data?.addContractLineItem?.success === true;
      } catch {
        return false;
      } finally {
        setIsAdding(false);
      }
    },
    [runMutation]
  );

  return useMemo(() => ({ addLineItem, isAdding }), [addLineItem, isAdding]);
}
